import { useState } from "react";
import { toast } from "sonner";
import { PencilSimple, ArrowClockwise, DownloadSimple, ArrowRight } from "@phosphor-icons/react";
import { PromoPageHeader } from "@/promotions/components/PromoPageHeader";
import { PromoSelect } from "@/promotions/components/PromoSelect";
import { EmptyState } from "@/promotions/components/EmptyState";
import { StatusBadge } from "@/promotions/components/StatusBadge";
import { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Field, TextArea } from "../components/Modal";
import { MediaImage } from "../components/MediaImage";
import { useCreativeData, creativeApi, pollJobUntilDone, fetchMediaMap, mediaUrl } from "../useCreative";
import { ASPECT_RATIOS } from "../creativeUtils";

export default function Edit() {
  const { assets, mediaMap, loading } = useCreativeData();
  const [selected, setSelected] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("1:1");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  // Only image assets that have stored bytes can be used as an edit source.
  const editable = assets.filter((a) => a.kind === "image" && a.media_asset_id && a.status !== "archived");

  const onGenerate = async () => {
    if (!selected) return toast.error("Select a source image");
    if (!prompt.trim()) return toast.error("Describe the edit you want");
    setGenerating(true);
    setResult(null);
    try {
      let job = await creativeApi.generate({
        kind: "image",
        operation: "edit",
        prompt: prompt.trim(),
        aspect_ratio: aspect || null,
        source_asset_id: selected.media_asset_id,
      }, `edit-${Date.now()}`);
      if (["queued", "running"].includes(job.status)) job = await pollJobUntilDone(job.id);
      if (job.status !== "completed") {
        toast.error(job.error || `Edit ${job.status}`);
        setResult({ job, assets: [] });
        return;
      }
      const [allAssets, media] = await Promise.all([creativeApi.listAssets(), fetchMediaMap()]);
      const jobAssets = allAssets.filter((a) => a.job_id === job.id).map((a) => ({ ...a, url: mediaUrl(media, a.media_asset_id) }));
      setResult({ job, assets: jobAssets });
      toast.success("Edit generated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="creative-edit">
      <PromoPageHeader overline="Creative Studio" title="Edit" description="Pick an existing asset as a reference and describe the changes — the provider re-renders it as a new creative." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Source picker */}
        <div className="space-y-3 lg:col-span-1" data-testid="edit-source-picker">
          <h3 className="text-sm font-medium">1 · Choose source</h3>
          {loading ? (
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-square rounded-md promo-skeleton" />)}
            </div>
          ) : editable.length === 0 ? (
            <EmptyState Icon={PencilSimple} title="No editable assets" description="Generate an image first, then edit it here." />
          ) : (
            <div className="grid max-h-[420px] grid-cols-3 gap-2 overflow-y-auto pr-1" data-testid="edit-source-grid">
              {editable.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  className={cn(
                    "overflow-hidden rounded-md border transition-colors",
                    selected?.id === a.id ? "border-brand ring-2 ring-brand/40" : "border-border hover:border-brand/40",
                  )}
                  data-testid={`edit-source-${a.id}`}
                >
                  <div className="aspect-square w-full">
                    <MediaImage url={mediaUrl(mediaMap, a.media_asset_id, "thumbnail")} className="h-full w-full object-cover" iconSize={16} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Edit controls */}
        <div className="space-y-5 rounded-lg border border-border bg-card p-6 lg:col-span-2" data-testid="edit-controls">
          <h3 className="text-sm font-medium">2 · Describe the edit</h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="overflow-hidden rounded-md border border-border">
              <div className="border-b border-border px-3 py-1.5 text-xs text-muted-foreground">Source</div>
              <div className="aspect-square w-full bg-background/40">
                <MediaImage url={selected ? mediaUrl(mediaMap, selected.media_asset_id) : null} className="h-full w-full object-contain" />
              </div>
            </div>
            <div className="overflow-hidden rounded-md border border-border">
              <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
                <span>Result</span>
                {result?.job && <StatusBadge status={result.job.status} dot={false} />}
              </div>
              <div className="flex aspect-square w-full items-center justify-center bg-background/40" data-testid="edit-result">
                {generating ? (
                  <ArrowClockwise size={22} className="animate-spin text-ai" />
                ) : result?.assets?.[0]?.url ? (
                  <MediaImage url={result.assets[0].url} className="h-full w-full object-contain" />
                ) : (
                  <ArrowRight size={22} className="text-muted-foreground" />
                )}
              </div>
            </div>
          </div>

          <Field label="Edit instructions">
            <TextArea rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Replace the background with a warm sunset sky and add soft rim lighting." data-testid="input-edit-prompt" />
          </Field>

          <div className="flex items-end gap-3">
            <Field label="Aspect ratio" className="w-40">
              <PromoSelect value={aspect} onChange={setAspect} options={ASPECT_RATIOS.map((r) => ({ value: r, label: r }))} testId="edit-select-aspect" />
            </Field>
            <button
              onClick={onGenerate}
              disabled={generating || !selected}
              className="flex flex-1 items-center justify-center gap-2 rounded-md bg-ai px-4 py-2 text-sm font-medium text-ai-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
              data-testid="btn-edit-generate"
            >
              {generating ? <ArrowClockwise size={16} className="animate-spin" /> : <PencilSimple size={16} weight="fill" />}
              {generating ? "Editing…" : "Generate edit"}
            </button>
          </div>

          {result?.assets?.[0]?.url && (
            <a href={result.assets[0].url} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-1 text-sm text-brand hover:underline" data-testid="edit-download">
              <DownloadSimple size={14} /> Download result
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
